/**
 * The version row: which versions a question reads, as pure rules.
 *
 * One row per browser and corpus, shared with the reading pages
 * (docs/more-bible-translations.md, "Part C"). The pinned versions — `pinned` in
 * /api/corpus, the original and the primary translation — are always in it and are never
 * stored. The ADDED codes live in localStorage `sourcetext.versions.<corpus>` as a JSON
 * list of canonical codes (`"KJV"`, not `"kjv"`); `pipeline/passage.js` reads and writes
 * the same key in the same format on the same origin. A finished answer adds any version a
 * tool read. Nothing here writes to the account: the AI never changes the start set, only
 * the account page does.
 */

import { createContext } from 'react'
import type { Account } from './account'

/** The row's versions (pinned + added), for cards that show text; null = not known yet. */
export const ActiveVersionsContext = createContext<string[] | null>(null)

/** The account's saved start set for one corpus, or null when there is no account or nothing is saved. */
export function savedSet(account: Account | null, corpusCode: string | null): string[] | null {
  if (corpusCode === null) return null
  const saved = account?.start_versions?.[corpusCode]
  return saved && saved.length > 0 ? [...saved] : null
}

/**
 * The set behind a finished answer: the set the server resolved (falling back to the one
 * sent), plus what the tools read. Null when neither side named a set.
 */
export function setAfterAnswer(
  sent: string[] | null,
  summary: { active_codes?: string[]; read_versions?: string[] },
): string[] | null {
  const resolved = summary.active_codes ?? sent
  return resolved === null ? null : withRead(resolved, summary.read_versions ?? [])
}

/** The set plus every code a tool read that it did not hold yet, in the order first seen. */
export function withRead(set: string[], read: string[]): string[] {
  const next = [...set]
  for (const code of read) {
    if (!next.includes(code)) next.push(code)
  }
  return next
}

/**
 * The added codes after an answer: every code of `answered` the row does not hold yet joins
 * it, after the ones it has. Pinned codes never join — they are in the row already.
 */
export function addAnswered(added: string[], answered: string[], pinned: string[]): string[] {
  const fresh = answered.filter((code) => !pinned.includes(code) && !added.includes(code))
  return fresh.length > 0 ? [...added, ...fresh] : added
}

/** The added codes this corpus still offers, without the pinned ones (always in the row anyway). */
export function addableOnly(added: string[], addable: string[], pinned: string[]): string[] {
  return added.filter((code) => addable.includes(code) && !pinned.includes(code))
}

/** What an ask sends: the pinned versions, then the added ones this corpus still offers. */
export function rowForAsk(pinned: string[], added: string[], addable: string[]): string[] {
  return [...pinned, ...addableOnly(added, addable, pinned)]
}

/** Versions the reader may still add, grouped by language, each group in the corpus's order. */
export function offerable<V extends { code: string; language: string }>(
  versions: V[],
  active: string[],
): { language: string; versions: V[] }[] {
  const groups = new Map<string, V[]>()
  for (const version of versions) {
    if (active.includes(version.code)) continue
    groups.set(version.language, [...(groups.get(version.language) ?? []), version])
  }
  return [...groups].map(([language, held]) => ({ language, versions: held }))
}

/** The storage key for one corpus's row — the reading pages use the same one. */
export function rowKey(corpusCode: string): string {
  return `sourcetext.versions.${corpusCode}`
}

/**
 * The added codes this browser keeps for a corpus. Null when nothing is stored (or storage
 * is refused), so a row the reader emptied on purpose — `[]` — is told apart from one never
 * chosen, and is not refilled from the start set on the next load.
 */
export function readStoredRow(corpusCode: string): string[] | null {
  try {
    const stored = window.localStorage.getItem(rowKey(corpusCode))
    if (stored === null) return null
    const parsed: unknown = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed.filter((code): code is string => typeof code === 'string') : null
  } catch {
    // Refused storage or a value some other build wrote: treated as nothing stored.
    return null
  }
}

/** Remember the added codes for a corpus. Refused or full storage only costs remembering them. */
export function writeStoredRow(corpusCode: string, added: string[]): void {
  try {
    window.localStorage.setItem(rowKey(corpusCode), JSON.stringify(added))
  } catch {
    // Refused storage only costs remembering the row; this page still uses it until it closes.
  }
}

/**
 * The added codes a browser with no stored row starts from: the non-pinned part of the
 * account's saved start set (which the account page saves as pinned + additions).
 */
export function startAdditions(account: Account | null, corpusCode: string, pinned: string[]): string[] {
  return (savedSet(account, corpusCode) ?? []).filter((code) => !pinned.includes(code))
}

/** One change to the added codes, applied to whatever the row holds when it applies. */
export type RowChange = (added: string[]) => string[]

/**
 * The chat's row as state. `added` is null until it is known — from storage, else from the
 * start set once the account has loaded. A change made before then waits in `pending` and
 * applies to the seeded row, so it can neither be lost nor overwrite a stored row unseen.
 * `toStore` says whether this row came from the reader or a seed worth sharing (written
 * to storage), or from storage itself (never written back).
 */
export interface RowState {
  added: string[] | null
  pending: RowChange[]
  toStore: boolean
}

export const UNKNOWN_ROW: RowState = { added: null, pending: [], toStore: false }

/** The row replaced by `added` (from storage or a seed), with any waiting changes applied after it. */
export function replaced(state: RowState, added: string[], toStore: boolean): RowState {
  const applied = state.pending.reduce((row, change) => change(row), added)
  return { added: applied, pending: [], toStore: toStore || state.pending.length > 0 }
}

/** The row seeded once: a no-op when it is already known. */
export function seeded(state: RowState, added: string[], toStore: boolean): RowState {
  return state.added === null ? replaced(state, added, toStore) : state
}

/** A change from the reader or an answer: applied now, or held until the row is known. */
export function changed(state: RowState, change: RowChange): RowState {
  if (state.added === null) return { ...state, pending: [...state.pending, change] }
  return { added: change(state.added), pending: [], toStore: true }
}

/** A language's name in the reader's own language; the code itself where the browser does not know it. */
export function languageName(code: string): string {
  try {
    return new Intl.DisplayNames([navigator.language], { type: 'language' }).of(code) ?? code
  } catch {
    return code
  }
}
